/**
 * AuthProvider — owns the token lifecycle for the SPA.
 *
 * Responsibilities:
 *   - hold reactive auth state (so React components re-render on login/logout)
 *   - wire `setUnauthorizedHandler` so 401 responses trigger a refresh attempt
 *   - schedule a background refresh slightly before token expiry
 *   - expose `useAuth()` for downstream components
 *
 * Why a context and not module state alone:
 *   - Components want to re-render when the user logs out (hide admin nav, etc.)
 *   - Module state can't trigger re-renders without a subscription hook,
 *     which is essentially what context provides for free.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from "react";

import { authApi, setUnauthorizedHandler } from "../api";
import {
  clearAuth, getCurrentAuth, secondsUntilExpiry, setAuth, subscribeAuth,
  type AuthState,
} from "./store";


interface AuthContextValue {
  auth: AuthState | null;
  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  logout: () => void;
  /** Mint a dev-token (only works against a non-production os-core). */
  devLogin: (role?: string) => Promise<void>;
  /** True if the user has the named scope. */
  hasScope: (scope: string) => boolean;
}


const AuthContext = createContext<AuthContextValue | null>(null);


/** Refresh ~60s before expiry to avoid mid-request 401 storms. */
const REFRESH_LEEWAY_S = 60;


export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [auth, setAuthState] = useState<AuthState | null>(() => getCurrentAuth());
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);

  // -- token refresh ---------------------------------------------------------

  const doRefresh = useCallback(async (): Promise<string | null> => {
    // Coalesce concurrent refresh attempts — multiple 401s shouldn't burn
    // the refresh token rotation.
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = (async (): Promise<string | null> => {
      const current = getCurrentAuth();
      if (!current) return null;
      try {
        const r = await authApi.refresh(current.refreshToken);
        const next = setAuth(r.access_token, r.refresh_token);
        return next?.accessToken ?? null;
      } catch {
        clearAuth();
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();
    refreshPromiseRef.current = promise;
    return promise;
  }, []);

  // Wire the API client's 401 handler to use our refresh logic.
  useEffect(() => {
    setUnauthorizedHandler(doRefresh);
    return () => setUnauthorizedHandler(null);
  }, [doRefresh]);

  // Subscribe to external store mutations (e.g. setAuth called from login).
  useEffect(() => subscribeAuth(setAuthState), []);

  // Schedule a proactive refresh just before expiry.
  useEffect(() => {
    if (!auth) return;
    const seconds = secondsUntilExpiry();
    const wait = Math.max(5, seconds - REFRESH_LEEWAY_S) * 1000;
    const timer = setTimeout(() => { void doRefresh(); }, wait);
    return () => clearTimeout(timer);
  }, [auth, doRefresh]);

  // -- public actions --------------------------------------------------------

  const login = useCallback(async (email: string, password: string, tenantSlug?: string) => {
    const r = await authApi.login({ email, password, ...(tenantSlug ? { tenant_slug: tenantSlug } : {}) });
    setAuth(r.access_token, r.refresh_token);
  }, []);

  const devLogin = useCallback(async (role = "admin") => {
    const r = await authApi.devToken(role);
    setAuth(r.access_token, r.refresh_token);
  }, []);

  const logout = useCallback(() => clearAuth(), []);

  const hasScope = useCallback((scope: string) => {
    return auth?.claims.scopes.includes(scope) ?? false;
  }, [auth]);

  const value = useMemo<AuthContextValue>(() => ({
    auth, login, logout, devLogin, hasScope,
  }), [auth, login, logout, devLogin, hasScope]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
